import React, { useContext, useEffect, useState } from 'react';
import swal from 'sweetalert2';
import Workflow from '../../../components/Workflow';
import { useDataSets } from '../../../contexts/DataSetsContext';
import { AppContext } from '../../../contexts/AppContext';
import { runIngestionWorkflow } from "../../../services/data";
import GenerateWorkflowDialog from '../../../components/Workflows/GenerateWorkflowDialog';
import { Button } from '../../../components/StyledElements';
import { extractGeneratedGraph, loadGeneratedWorkflowGraph } from '../../../services/workflowGeneration';
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const IngestWorkflow = ({ style }) => {
  const { updatedDataSet, setUpdatedDataSet, getDefaultIngestionWorkflow } = useDataSets();
  const { selectedClient } = useContext(AppContext);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // The editor captures its initial graph at mount, so a generated graph is applied by
  // remounting it rather than by swapping the prop.
  const [graphVersion, setGraphVersion] = useState(0);

  console.log("!!! UPDATED DATA SET IN WORKFLOW: ", updatedDataSet);
  useEffect(() => {
    if (updatedDataSet && !updatedDataSet.ingestion_workflow) {
      const defaultWorkflow = getDefaultIngestionWorkflow();
      setUpdatedDataSet({ ...updatedDataSet, ingestion_workflow: defaultWorkflow });
    }
    // eslint-disable-next-line
  }, [updatedDataSet]);

  if (!updatedDataSet) {
    return <div>Loading...</div>;
  }

  console.log("!!! INGEST WORKFLOW: ", updatedDataSet.ingestion_workflow);

  const applyGeneratedWorkflow = async (saved, results) => {
    setShowGenerateDialog(false);

    const inlineGraph = extractGeneratedGraph(results);
    const generatedGraph = inlineGraph ?? (await loadGeneratedWorkflowGraph(saved?.public_id))?.graph ?? null;

    if (!generatedGraph) {
      swal.fire({
        title: "Generated",
        text: "The workflow was generated, but no graph came back, so it could not be loaded here.",
        icon: "info",
      });
      return;
    }

    setUpdatedDataSet({ ...updatedDataSet, ingestion_workflow: generatedGraph });
    setGraphVersion((version) => version + 1);

    swal.fire({
      title: "Ingestion workflow generated",
      text: `"${saved?.name ?? results?.name ?? "Workflow"}" was loaded into this data set's ingestion workflow. It replaces the graph below — click Save Changes to keep it.`,
      icon: "success",
    });
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "10px", ...style }}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <div style={{ fontSize: "13px", color: "#a0a6b1", flex: 1 }}>
          Describe how documents should be ingested and let the generator build the graph. Ingestion graphs must end in a Chroma input node.
        </div>
        <Button onClick={() => setShowGenerateDialog(true)}>Generate from description</Button>
      </div>

      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Workflow
          key={graphVersion}
          workflow={updatedDataSet.ingestion_workflow}
          onWorkflowChange={(workflow) => {
            const newDataSet = { ...updatedDataSet };
            newDataSet.ingestion_workflow = workflow;
            setUpdatedDataSet(newDataSet);
          }}
          onRunWorkflow={() => {
            console.log("!!! RUNNING WORKFLOW: ", updatedDataSet.ingestion_workflow);
            // Here you would typically call a service to run the workflow
            // For example:
            runIngestionWorkflow(updatedDataSet.public_id);
          }}
        />
      </div>

      <GenerateWorkflowDialog
        show={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerated={applyGeneratedWorkflow}
        defaultName={updatedDataSet?.name ? `${updatedDataSet.name} ingestion` : ""}
        defaultDescription={updatedDataSet?.description ?? ""}
        defaultPurpose={updatedDataSet?.purpose ?? ""}
        defaultKind="ingestion"
        clientId={resolveGeneratorClientId(selectedClient, updatedDataSet)}
      />
    </div>
  );
};

export default IngestWorkflow;
